import React, { useState } from 'react'
import {
    Box,
    Collapse,
    IconButton,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material'
import {
    InfoOutlined,
    KeyboardArrowDown,
    KeyboardArrowRight,
    StorefrontOutlined,
    SettingsSuggestOutlined,
} from '@mui/icons-material'
import dayjs from 'dayjs'
import tzlookup from 'tz-lookup'
import MetricsPanel from './MetricsPanel'
import FaultLog, {
    FaultFilters,
    FaultFilterKey,
    formatFaultTs,
} from './FaultLog'

const ALARM_COLOR = '#F59E0B' // orange
const DTC_COLOR = '#E00000' // red

export interface ServiceRow {
    id: string
    hin: string
    model?: string
    alarms: number
    dtcs: number
    latest: number
    /** Alarms+DTCs per 100 engine-hours over the window (null if no hours). */
    rate?: number | null
    hours?: number
    lat?: number
    lng?: number
    /** Offline reverse-geocoded location (resolved server-side). */
    city?: string
    state?: string
    country?: string
}

// Two-line location label: "City, ST" over "Country". Shared with the Boats tab.
export const BoatLocation: React.FC<{
    city?: string
    state?: string
    country?: string
}> = ({ city, state, country }) => {
    const line1 = [city, state].filter(Boolean).join(', ')
    if (!line1 && !country)
        return (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                —
            </Typography>
        )
    return (
        <Box>
            {line1 && (
                <Typography
                    sx={{
                        fontSize: 11,
                        lineHeight: 1.25,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {line1}
                </Typography>
            )}
            {country && (
                <Typography
                    sx={{
                        fontSize: 10,
                        lineHeight: 1.2,
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {country}
                </Typography>
            )}
        </Box>
    )
}

export interface ServiceEvent {
    type: 'alarm' | 'dtc'
    ts: number
    day?: string
    description: string
    code?: string
    severity?: number | null
    /** Nearest end-of-day lifetime engine hours around the fault. */
    engineHours?: number | null
}

// Lazily-loaded per-boat detail for the expansion (faults + metadata + metrics).
export interface BoatDetailData {
    events: ServiceEvent[]
    hin?: string
    model?: string
    dealer?: string | null
    /** Per-day lifetime engine hours min/max (for the metrics chart). */
    hoursByDay?: Record<string, { lo: number; hi: number }>
}

// Per-day stacked bars (alarms orange + DTCs red) across the window.
const DailyFaultBars: React.FC<{
    events: ServiceEvent[]
    days?: string[]
}> = ({ events, days }) => {
    const byDay: Record<string, { a: number; t: number }> = {}
    events.forEach((e) => {
        if (!e.day) return
        if (!byDay[e.day]) byDay[e.day] = { a: 0, t: 0 }
        if (e.type === 'alarm') byDay[e.day].a += 1
        else byDay[e.day].t += 1
    })
    const axis =
        days && days.length
            ? days
            : Object.keys(byDay).sort()
    if (!axis.length) return null
    const maxTotal = Math.max(
        1,
        ...axis.map((d) => (byDay[d]?.a || 0) + (byDay[d]?.t || 0))
    )
    return (
        <Box sx={{ mt: 1.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 0.5,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                    Daily faults
                </Typography>
                {[
                    { c: ALARM_COLOR, l: 'Alarms' },
                    { c: DTC_COLOR, l: 'DTCs' },
                ].map((x) => (
                    <Box
                        key={x.l}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '2px',
                                bgcolor: x.c,
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.disabled', fontSize: 10 }}
                        >
                            {x.l}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '2px',
                    height: 46,
                }}
            >
                {axis.map((d) => {
                    const a = byDay[d]?.a || 0
                    const t = byDay[d]?.t || 0
                    return (
                        <Tooltip
                            key={d}
                            arrow
                            placement="top"
                            title={`${dayjs(d).format('MMM D')} · ${a} alarm${
                                a !== 1 ? 's' : ''
                            }, ${t} DTC${t !== 1 ? 's' : ''}`}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 3,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                {t > 0 && (
                                    <Box
                                        sx={{
                                            height: `${(t / maxTotal) * 100}%`,
                                            bgcolor: DTC_COLOR,
                                            borderRadius: '2px 2px 0 0',
                                        }}
                                    />
                                )}
                                {a > 0 && (
                                    <Box
                                        sx={{
                                            height: `${(a / maxTotal) * 100}%`,
                                            bgcolor: ALARM_COLOR,
                                            borderRadius:
                                                t > 0 ? 0 : '2px 2px 0 0',
                                        }}
                                    />
                                )}
                                {a === 0 && t === 0 && (
                                    <Box
                                        sx={{
                                            height: 2,
                                            bgcolor: 'rgba(255,255,255,0.06)',
                                        }}
                                    />
                                )}
                            </Box>
                        </Tooltip>
                    )
                })}
            </Box>
        </Box>
    )
}

const CountPill: React.FC<{ n: number; color: string; label: string }> = ({
    n,
    color,
    label,
}) => (
    <Box
        component="span"
        title={label}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 26,
            px: 0.75,
            py: 0.1,
            borderRadius: 1,
            fontSize: 12,
            fontWeight: 700,
            color: n > 0 ? color : 'text.disabled',
            border: '1px solid',
            borderColor: n > 0 ? color : 'divider',
            bgcolor: n > 0 ? `${color}1A` : 'transparent',
        }}
    >
        {n}
    </Box>
)

interface Props {
    rows: ServiceRow[]
    title?: string
    rangeLabel?: string
    tabs?: React.ReactNode
    /** Render bare (no card chrome / title / tabs) — a shell owns those. */
    embedded?: boolean
    onExpand: (serial: string) => void
    detailBySerial: Record<string, BoatDetailData | undefined>
    loadingSerial?: string | null
    /** Window day axis for the per-boat daily-fault bars. */
    days?: string[]
    /** Timezone for event timestamps: browser, each boat's local, or UTC. */
    tzMode?: 'browser' | 'boat' | 'utc'
    /** Fires when a row expands (with its GPS) or collapses (null) — used to
     *  focus the fleet map on the boat / reset it. */
    onExpandChange?: (
        serial: string | null,
        lat?: number,
        lng?: number
    ) => void
}

// Resolve a boat row's display zone from the tz mode (boat-local needs GPS).
const rowTz = (
    tzMode: 'browser' | 'boat' | 'utc' | undefined,
    lat?: number,
    lng?: number
): string | undefined => {
    if (tzMode === 'utc') return 'UTC'
    if (tzMode === 'boat' && lat != null && lng != null) {
        try {
            return tzlookup(lat, lng)
        } catch {
            return undefined
        }
    }
    return undefined
}

export const ServiceTable: React.FC<Props> = ({
    rows,
    title = 'Top Performers',
    rangeLabel,
    tabs,
    embedded,
    onExpand,
    detailBySerial,
    loadingSerial,
    days,
    tzMode,
    onExpandChange,
}) => {
    const [open, setOpen] = useState<string | null>(null)
    // Per-expansion sub-tab (only one row open at a time). Resets on open.
    const [expTab, setExpTab] = useState<'faults' | 'metrics'>('faults')
    const [faultFilter, setFaultFilter] = useState<FaultFilterKey>('all')
    const toggle = (serial: string) => {
        if (open === serial) {
            setOpen(null)
            onExpandChange?.(null)
            return
        }
        setOpen(serial)
        setExpTab('faults')
        const row = rows.find((r) => r.id === serial)
        onExpandChange?.(serial, row?.lat, row?.lng)
        if (!detailBySerial[serial]) onExpand(serial)
    }
    return (
        <Paper
            elevation={0}
            sx={{
                overflow: 'hidden',
                height: '100%',
                ...(embedded && {
                    bgcolor: 'transparent',
                    backgroundImage: 'none',
                }),
            }}
        >
            {!embedded && (
            <Box
                sx={{
                    px: 2.5,
                    pt: 2,
                    pb: tabs ? 0.5 : 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Tooltip
                    arrow
                    placement="top"
                    title={`Alarms & DTCs (deduped to one per fault/day)${
                        rangeLabel ? ` · ${rangeLabel}` : ''
                    } — boats ordered by most recent fault. Click a boat for its fault timeline.`}
                >
                    <InfoOutlined
                        sx={{
                            fontSize: 15,
                            color: 'text.disabled',
                            cursor: 'help',
                        }}
                    />
                </Tooltip>
            </Box>
            )}
            {!embedded && tabs}
            <Box sx={{ overflowX: 'auto' }}>
                <Table
                    size="small"
                    sx={{
                        minWidth: 560,
                        '& td:first-of-type, & th:first-of-type': { pl: 2.5 },
                        '& td:last-of-type, & th:last-of-type': { pr: 2.5 },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>Boat</TableCell>
                            <TableCell>Location</TableCell>
                            <TableCell align="center">Alarms</TableCell>
                            <TableCell align="center">DTCs</TableCell>
                            <TableCell align="center">
                                <Tooltip
                                    arrow
                                    placement="top"
                                    title="Alarms + DTCs per 100 engine-hours over the period"
                                >
                                    <span style={{ cursor: 'help' }}>
                                        /100h
                                    </span>
                                </Tooltip>
                            </TableCell>
                            <TableCell>Latest</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    sx={{ color: 'text.secondary' }}
                                >
                                    No alarms or DTCs in this period.
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((r) => {
                                const isOpen = open === r.id
                                const detail = detailBySerial[r.id]; const events = detail?.events
                                return (
                                    <React.Fragment key={r.id}>
                                        <TableRow
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => toggle(r.id)}
                                        >
                                            <TableCell
                                                sx={{ maxWidth: 180 }}
                                                title={r.hin}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0 }}
                                                    >
                                                        {isOpen ? (
                                                            <KeyboardArrowDown fontSize="small" />
                                                        ) : (
                                                            <KeyboardArrowRight fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {r.hin}
                                                        </Typography>
                                                        {r.model && (
                                                            <Typography
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: 10,
                                                                    lineHeight: 1.2,
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {r.model}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 150 }}>
                                                <BoatLocation
                                                    city={r.city}
                                                    state={r.state}
                                                    country={r.country}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <CountPill
                                                    n={r.alarms}
                                                    color={ALARM_COLOR}
                                                    label="Alarms"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <CountPill
                                                    n={r.dtcs}
                                                    color={DTC_COLOR}
                                                    label="DTCs"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                        color:
                                                            r.rate == null
                                                                ? 'text.disabled'
                                                                : 'text.primary',
                                                        fontVariantNumeric:
                                                            'tabular-nums',
                                                    }}
                                                >
                                                    {r.rate == null
                                                        ? '—'
                                                        : r.rate.toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {r.latest
                                                        ? formatFaultTs(
                                                              r.latest,
                                                              rowTz(
                                                                  tzMode,
                                                                  r.lat,
                                                                  r.lng
                                                              )
                                                          )
                                                        : '—'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                sx={{
                                                    py: 0,
                                                    border: isOpen
                                                        ? undefined
                                                        : 'none',
                                                }}
                                            >
                                                <Collapse
                                                    in={isOpen}
                                                    unmountOnExit
                                                >
                                                    <Box sx={{ py: 1, pl: 4, pr: 2 }}>
                                                        {/* Metadata: dealer · location · model */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                alignItems:
                                                                    'center',
                                                                gap: 2,
                                                                mb: 1,
                                                                color: 'text.secondary',
                                                            }}
                                                        >
                                                            {detail?.dealer && (
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 0.5,
                                                                    }}
                                                                >
                                                                    <StorefrontOutlined
                                                                        sx={{
                                                                            fontSize: 15,
                                                                        }}
                                                                    />
                                                                    <Typography variant="body2">
                                                                        {
                                                                            detail.dealer
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {r.hours != null &&
                                                                r.hours > 0 && (
                                                                    <Box
                                                                        title="Engine hours in period"
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                            gap: 0.5,
                                                                        }}
                                                                    >
                                                                        <SettingsSuggestOutlined
                                                                            sx={{
                                                                                fontSize: 15,
                                                                            }}
                                                                        />
                                                                        <Typography variant="body2">
                                                                            {r.hours.toLocaleString()}{' '}
                                                                            h
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                        </Box>
                                                        {/* Faults | Metrics tabs; the fault filter shares this row, far right */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'space-between',
                                                                flexWrap: 'wrap',
                                                                gap: 1,
                                                                mb: 1,
                                                            }}
                                                        >
                                                        <Tabs
                                                            value={expTab}
                                                            onChange={(_e, v) =>
                                                                setExpTab(v)
                                                            }
                                                            sx={{
                                                                minHeight: 34,
                                                                '& .MuiTab-root':
                                                                    {
                                                                        minHeight: 34,
                                                                        py: 0.25,
                                                                        minWidth:
                                                                            'auto',
                                                                        mr: 2,
                                                                        px: 0,
                                                                        textTransform:
                                                                            'none',
                                                                        fontWeight: 600,
                                                                    },
                                                            }}
                                                        >
                                                            <Tab
                                                                label="Faults"
                                                                value="faults"
                                                            />
                                                            <Tab
                                                                label="Metrics"
                                                                value="metrics"
                                                            />
                                                        </Tabs>
                                                        {expTab === 'faults' &&
                                                            events && (
                                                                <FaultFilters
                                                                    rows={events}
                                                                    filter={
                                                                        faultFilter
                                                                    }
                                                                    onFilter={
                                                                        setFaultFilter
                                                                    }
                                                                />
                                                            )}
                                                        </Box>
                                                        {!events ? (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                }}
                                                            >
                                                                {loadingSerial ===
                                                                r.id
                                                                    ? 'Loading events…'
                                                                    : '…'}
                                                            </Typography>
                                                        ) : expTab ===
                                                          'metrics' ? (
                                                            <MetricsPanel
                                                                detail={detail!}
                                                                days={days}
                                                            />
                                                        ) : (
                                                            <>
                                                                <FaultLog
                                                                    rows={events}
                                                                    filter={
                                                                        faultFilter
                                                                    }
                                                                    tz={rowTz(
                                                                        tzMode,
                                                                        r.lat,
                                                                        r.lng
                                                                    )}
                                                                />
                                                                <DailyFaultBars
                                                                    events={
                                                                        events
                                                                    }
                                                                    days={days}
                                                                />
                                                            </>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
    )
}

export default ServiceTable
